import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { MapPin, ArrowRight, Maximize, Users, Dumbbell } from "lucide-react";

const clubs = [
  {
    id: "maia",
    name: "Clube da Maia",
    location: "Parada, Maia",
    tagline: "O Melhor Ginásio do Porto",
    image: "/images/clube-maia-card.webp",
    stats: {
      area: "3000 m²",
      studios: "4 Estúdios",
      classes: "100+ Aulas",
    },
    highlights: ["Estacionamento Privativo", "Pilates & Aeropilates", "Luz Natural"],
  },
  {
    id: "barcelos",
    name: "Clube de Barcelos",
    location: "Centro de Barcelos",
    tagline: "No Coração da Cidade",
    image: "/images/clube-barcelos-card.webp",
    stats: {
      area: "1000 m²",
      studios: "3 Estúdios",
      classes: "100+ Aulas",
    },
    highlights: ["Vista Parque Urbano", "Pilates", "Espaço Amplo"],
  },
];

export const Clubs = () => {
  const navigate = useNavigate();

  return (
    <section id="clubes" className="py-20 lg:py-28 bg-[#1A1A1A]" data-testid="clubs-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <h2 className="text-4xl lg:text-5xl font-black text-white mb-4">
            Conhece os <span className="text-[#FED601]">Nossos Clubes</span>
          </h2>
        </motion.div>

        {/* Clubs Grid */}
        <div className="grid lg:grid-cols-2 gap-8">
          {clubs.map((club, index) => (
            <motion.div
              key={club.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="group bg-[#1F1F1F] rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer border border-white/[0.06] hover:border-[#FED601]/50"
              onClick={() => navigate(`/clube/${club.id}`)}
              data-testid={`club-card-${club.id}`}
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={club.image}
                  alt={club.name}
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                
                {/* Location Badge */}
                <div className="absolute top-4 left-4 flex items-center gap-2 bg-white text-[#1A1A1A] px-4 py-2 rounded-full shadow">
                  <MapPin size={16} className="text-[#FED601]" />
                  <span className="font-medium text-sm">{club.location}</span>
                </div>

                {/* Club Name */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-3xl font-bold text-white mb-1">
                    {club.name}
                  </h3>
                  <p className="text-white/80">{club.tagline}</p>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-3 bg-[#2A2A2A] rounded-lg">
                    <Maximize size={18} className="text-[#FED601] mx-auto mb-1" />
                    <p className="font-bold text-white text-sm">{club.stats.area}</p>
                  </div>
                  <div className="text-center p-3 bg-[#2A2A2A] rounded-lg">
                    <Users size={18} className="text-[#FED601] mx-auto mb-1" />
                    <p className="font-bold text-white text-sm">{club.stats.studios}</p>
                  </div>
                  <div className="text-center p-3 bg-[#2A2A2A] rounded-lg">
                    <Dumbbell size={18} className="text-[#FED601] mx-auto mb-1" />
                    <p className="font-bold text-white text-sm">{club.stats.classes}</p>
                  </div>
                </div>

                {/* Highlights */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {club.highlights.map((highlight) => (
                    <span
                      key={highlight}
                      className="px-3 py-1 bg-[#FED601]/15 text-[#FED601] text-sm font-medium rounded-full"
                    >
                      {highlight}
                    </span>
                  ))}
                </div>

                {/* CTA */}
                <button
                  className="w-full flex items-center justify-center gap-2 py-3 bg-[#FED601] text-black font-bold uppercase tracking-wider rounded-lg group-hover:bg-[#E0BC00] transition-colors duration-300"
                  data-testid={`club-cta-${club.id}`}
                >
                  <span>Conhecer Clube</span>
                  <ArrowRight size={18} className="transform group-hover:translate-x-2 transition-transform" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
